import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { FC, ReactNode, useMemo } from 'react';

import {
    BillingFormData, getProductsForSelection
} from '@/containers/logistic/purchaseOrders/wizard/NewPurchaseOrderWizard';
import { useBusinessCommercialsQuery } from '@/hooks/business/useBusinessCommercialsQuery';
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery';

interface ReviewStepProps {
  formData: BillingFormData
  onNext: (data: Partial<BillingFormData>) => void // Typically doesn't pass data back
}

// Helper to display data safely
const DisplayData: FC<{
  label: string
  value?: string | number | Date | boolean | null | { [key: string]: number }
  isDate?: boolean
  isDateTime?: boolean
  isCurrency?: boolean
  currencyCode?: string
  isBool?: boolean
  isSubtotals?: boolean
}> = ({
  label,
  value,
  isDate,
  isDateTime,
  isCurrency,
  currencyCode,
  isBool,
  isSubtotals,
}) => {
  let displayValue: string | number | null | ReactNode = null

  if (isBool) {
    displayValue = value ? 'Sí' : 'No'
  } else if (value instanceof Date && isDateTime) {
    displayValue = format(value, 'PPP HH:mm', { locale: es })
  } else if (value instanceof Date && isDate) {
    displayValue = format(value, 'PPP', { locale: es })
  } else if (isCurrency && typeof value === 'number') {
    if (value > 0 || value === 0) {
      displayValue = `${value.toFixed(2)} ${currencyCode || ''}`
    }
  } else if (value !== undefined && value !== null && value !== '') {
    displayValue = value.toString()
  }

  if (displayValue === null) return null

  return (
    <div className="flex justify-between border-b py-1.5 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}:</span>
      {isSubtotals ? (
        <div className="flex flex-col justify-end font-medium">
          {Object.entries(value as { [key: string]: number }).map(
            ([currency, total]) => (
              <span key={currency} className="text-right text-sm font-medium">
                {total.toFixed(2)} {currency}
              </span>
            )
          )}
        </div>
      ) : (
        <span className="text-right text-sm font-medium">{displayValue}</span>
      )}
    </div>
  )
}

const ReviewStep: React.FC<ReviewStepProps> = ({ formData, onNext }) => {
  // Use new React Query hooks instead of Redux for better error handling
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})
  const { businessCommercials, isLoadingBusinessCommercials } =
    useBusinessCommercialsQuery({
      businessId: businessData?.id ?? 0,
      options: {
        enabled:
          !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
      },
    })


  const handleNextClick = () => {
    onNext({}) // Pass empty object as review step doesn't modify data
  }

  // Get current selection's products using the new structure
  const currentProducts = useMemo(() => {
    if (formData.selectedAreaSalesId && formData.selectedPriceSystemId) {
      return getProductsForSelection(
        formData.productSelectionsByAreaAndPriceSystem ?? {},
        formData.selectedAreaSalesId,
        formData.selectedPriceSystemId
      )
    }

    return []
  }, [
    formData.productSelectionsByAreaAndPriceSystem,
    formData.selectedAreaSalesId,
    formData.selectedPriceSystemId,
    formData.products,
  ])

  // Resolve client name
  const clientName = useMemo(() => {
    if (formData.isNewClient) {
      const clientData = formData.newClientData
      return clientData
        ? `${clientData.name || ''} ${clientData.lastName || ''}`.trim()
        : 'Nuevo Proveedor'
    } else if (formData.clientId) {
      return `${formData.selectedClientData?.firstName || ''} ${formData.selectedClientData?.lastName || ''}`.trim()
    }
    return 'N/A'
  }, [formData.isNewClient, formData.newClientData, formData.clientId])

  // Resolve commercial name
  const commercialName = useMemo(() => {
    if (formData.commercialId) {
      if (isLoadingBusinessCommercials) {
        return 'Cargando...'
      }
      const commercial = businessCommercials?.find(
        (user) => user.id && user.id.toString() === formData.commercialId
      )
      return commercial
        ? commercial.displayName || commercial.username
        : `ID: ${formData.commercialId}`
    }
    return 'N/A'
  }, [formData.commercialId, businessCommercials, isLoadingBusinessCommercials])



  const totalQuantity = useMemo(() => {
    return currentProducts.reduce((acc, product) => acc + product.quantity, 0)
  }, [currentProducts])

  const { totalWeight, totalVolume, currencyTotals } = useMemo(() => {
    const totals: {
      currencyTotals: { [key: string]: number }
      totalWeight: number
      totalVolume: number
    } = {
      currencyTotals: {},
      totalWeight: 0,
      totalVolume: 0,
    }
    currentProducts.forEach((p) => {
      if (!totals.currencyTotals[p.currency])
        totals.currencyTotals[p.currency] = 0
      totals.currencyTotals[p.currency] += p.totalPrice
      if (p.weight) totals.totalWeight += p.weight * p.quantity
      if (p.volume) totals.totalVolume += p.volume * p.quantity
    })
    return totals
  }, [currentProducts])

  const dueDate =
    formData.paymentTerm === 'Personalizado'
      ? formData.customDueDate
      : formData.calculatedDueDate

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Revisión Final</h3>
      <p className="text-sm text-muted-foreground">
        Por favor, verifica que toda la información sea correcta antes de crear
        la orden de compra.
      </p>

      {/* Client Info */}
      <div className="space-y-1 rounded-md border p-4">
        <h4 className="mb-2 text-base font-semibold">Proveedor</h4>
        <DisplayData label="Nombre y apellidos" value={clientName} />
        {formData.isNewClient && formData.newClientData?.lastName && (
          <DisplayData
            label="Apellidos"
            value={formData.newClientData.lastName}
          />
        )}
        {formData.isNewClient && formData.newClientData?.idNumber && (
          <DisplayData label="No. ID" value={formData.newClientData.idNumber} />
        )}
        {formData.isNewClient && formData.newClientData?.phone && (
          <DisplayData
            label="Teléfono"
            value={`${formData.newClientData.phone.codeCountry} ${formData.newClientData.phone.number}`}
          />
        )}
        {formData.isNewClient && formData.newClientData?.email && (
          <DisplayData label="Email" value={formData.newClientData.email} />
        )}
        {formData.isNewClient && formData.newClientData?.clientType && (
          <DisplayData label="Tipo" value={formData.newClientData.clientType} />
        )}
        {formData.isNewClient && (
          <DisplayData
            label="Dirección"
            value={
              [
                formData.newClientData?.mainAddress,
                formData.newClientData?.secondaryAddress,
                formData.newClientData?.locality,
                formData.newClientData?.municipality?.name,
                formData.newClientData?.province?.name,
                formData.newClientData?.country?.name,
              ]
                .filter(Boolean)
                .join(', ') || 'No especificada'
            }
          />
        )}
        {formData.isNewClient && formData.newClientData?.legalNotes && (
          <DisplayData
            label="Notas legales"
            value={formData.newClientData.legalNotes}
          />
        )}
        <DisplayData label="Notas (Internas)" value={formData.clientNotes} />
      </div>

      {/* Document Details */}
      <div className="space-y-1 rounded-md border p-4">
        <h4 className="mb-2 text-base font-semibold">Detalles de la Orden de Compra</h4>
        <DisplayData label="Título" value={formData.title} />
        <DisplayData label="Comercial" value={commercialName} />
        <DisplayData label="Plazo de pago" value={formData.paymentTerm} />
        {dueDate && (
          <DisplayData
            label="Fecha de vencimiento"
            value={dueDate}
            isDate={true}
          />
        )}
        <DisplayData label="Observaciones" value={formData.observations} />
      </div>

      {/* Products Summary */}
      {currentProducts && currentProducts.length > 0 && (
        <div className="space-y-1 rounded-md border p-4">
          <h4 className="mb-2 text-base font-semibold">
            Productos ({totalQuantity} elemento{totalQuantity !== 1 ? 's' : ''})
          </h4>
          <DisplayData
            label="Subtotal"
            value={currencyTotals}
            isCurrency
            isSubtotals
          />
          <DisplayData
            label="Peso total"
            value={totalWeight > 0 ? totalWeight : 0}
            isCurrency
            currencyCode="kg"
          />
          <DisplayData
            label="Volumen total"
            value={totalVolume > 0 ? totalVolume : 0}
            isCurrency
            currencyCode="m³"
          />
        </div>
      )}



      {/* Hidden button to trigger onNext from wizard footer */}
      <button
        onClick={handleNextClick}
        className="hidden"
        id="review-step-next"
      ></button>
    </div>
  )
}

export default ReviewStep
